import { Pencil, Plus, RefreshCw, Trash2, X } from "lucide-react";
import { FormEvent, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { ProductRule, ProductRulePayload } from "../types";

const blankRule: ProductRulePayload = {
  alias: "",
  canonical_name: "",
  category: "空调",
  sort_order: 100,
};

export function ProductRules() {
  const [rules, setRules] = useState<ProductRule[]>([]);
  const [form, setForm] = useState<ProductRulePayload>(blankRule);
  const [editingId, setEditingId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  async function load() {
    setBusy(true);
    setError("");

    try {
      const data = await api.rules();
      setRules(data.rules);
    } catch (err) {
      setError(err instanceof Error ? err.message : "加载失败");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function edit(rule: ProductRule) {
    setEditingId(rule.id);
    setForm({
      alias: rule.alias,
      canonical_name: rule.canonical_name,
      category: rule.category,
      sort_order: rule.sort_order,
    });
  }

  function reset() {
    setEditingId("");
    setForm(blankRule);
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    setMessage("");

    try {
      if (editingId) {
        await api.updateRule(editingId, form);
        setMessage("规则已更新");
      } else {
        await api.createRule(form);
        setMessage("规则已新增");
      }
      reset();
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    if (!window.confirm("确定删除这条规则吗？")) {
      return;
    }

    await api.deleteRule(id);
    await load();
  }

  return (
    <section className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Product Rules</p>
          <h1>商品规则</h1>
        </div>
        <button className="secondary-button" onClick={load} type="button">
          <RefreshCw size={17} aria-hidden />
          刷新
        </button>
      </header>

      {message ? <div className="notice success">{message}</div> : null}
      {error ? <div className="notice danger">{error}</div> : null}

      <section className="panel">
        <form className="rule-form" onSubmit={submit}>
          <label className="field">
            <span>识别词</span>
            <input
              onChange={(event) =>
                setForm((current) => ({ ...current, alias: event.target.value }))
              }
              required
              value={form.alias}
            />
          </label>
          <label className="field">
            <span>标准商品名</span>
            <input
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  canonical_name: event.target.value,
                }))
              }
              required
              value={form.canonical_name}
            />
          </label>
          <label className="field">
            <span>大类</span>
            <select
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  category: event.target.value,
                }))
              }
              value={form.category}
            >
              <option value="空调">空调</option>
              <option value="电视">电视</option>
              <option value="洗衣机">洗衣机</option>
              <option value="其他">其他</option>
            </select>
          </label>
          <label className="field">
            <span>排序</span>
            <input
              min="1"
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  sort_order: event.target.value,
                }))
              }
              type="number"
              value={form.sort_order}
            />
          </label>
          <div className="button-row">
            <button className="primary-button" disabled={busy} type="submit">
              {editingId ? (
                <Pencil size={17} aria-hidden />
              ) : (
                <Plus size={17} aria-hidden />
              )}
              {editingId ? "更新规则" : "新增规则"}
            </button>
            {editingId ? (
              <button className="secondary-button" onClick={reset} type="button">
                <X size={17} aria-hidden />
                取消
              </button>
            ) : null}
          </div>
        </form>
      </section>

      <section className="panel">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>排序</th>
                <th>识别词</th>
                <th>标准商品名</th>
                <th>大类</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rules.map((rule) => (
                <tr key={rule.id}>
                  <td>{rule.sort_order}</td>
                  <td>{rule.alias}</td>
                  <td>{rule.canonical_name}</td>
                  <td>{rule.category || "-"}</td>
                  <td>
                    <div className="icon-row">
                      <button
                        className="icon-button"
                        onClick={() => edit(rule)}
                        title="编辑规则"
                        type="button"
                      >
                        <Pencil size={16} aria-hidden />
                      </button>
                      <button
                        className="icon-button danger"
                        onClick={() => remove(rule.id)}
                        title="删除规则"
                        type="button"
                      >
                        <Trash2 size={16} aria-hidden />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {rules.length === 0 ? <p className="empty table-empty">暂无规则</p> : null}
        </div>
      </section>
    </section>
  );
}
